import axios from "axios";
import { api } from "./config";

export async function uploadPackagePhoto(trackingNumber, file, ext, location, category, onProgress, station) {
  const formData = new FormData();
  formData.append("trackingNumber", trackingNumber);
  formData.append("ext", ext); // send extension explicitly
  formData.append("lat", location.lat);
  formData.append("lng", location.lng);
  formData.append("photo", file);
  if (category) {
    formData.append("category", category);
  }
  if (station) {
    formData.append("station", station);
  }

  try {
    const response = await api().post(
      "/package/upload-photo", // match your backend route
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: onProgress
          ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
          : undefined,
      }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Uploads fragile-check photo evidence (multiple files) for a package, completing
// the given stage ("transit" | "final") so the scan gate (409 FRAGILE_CHECK_REQUIRED)
// clears. Video is never uploaded — videoReportConfirmed just records that the
// operator confirmed sending it to the chat group and reporting to the manager.
export async function completeFragileCheck(tracking, stage, stationId, files, location, videoReportConfirmed, onProgress) {
  const formData = new FormData();
  formData.append("tracking", tracking);
  formData.append("stage", stage);
  formData.append("station", stationId);
  formData.append("lat", location?.lat ?? "");
  formData.append("lng", location?.lng ?? "");
  formData.append("videoReportConfirmed", videoReportConfirmed ? "true" : "false");
  files.forEach((file) => formData.append("photos", file));

  try {
    const response = await api().post(
      "/package/fragile-check",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: onProgress
          ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
          : undefined,
      }
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Cargo Prep stage 1, step 1 of 2 — photo of everything going into one box,
// BEFORE sealing it. Shared across every packageId (they all go in the same box).
export async function cargoPrepItemsPhoto(customerId, packageIds, file, stationId) {
  const formData = new FormData();
  formData.append("customerId", customerId);
  formData.append("packageIds", JSON.stringify(packageIds));
  formData.append("station", stationId || "");
  formData.append("itemsPhoto", file);
  try {
    const response = await api().post("/package/cargo-prep-items-photo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    if (error.response) return { success: false, message: error.response.data?.message || "Failed to save items photo" };
    throw error;
  }
}

// Cargo Prep stage 1, step 2 of 2 — photo of the sealed box. Completes prep and
// triggers the "packed and ready to forward" WhatsApp notification.
export async function cargoPrepBoxPhoto(packageIds, file, stationId) {
  const formData = new FormData();
  formData.append("packageIds", JSON.stringify(packageIds));
  formData.append("station", stationId || "");
  formData.append("boxPhoto", file);
  try {
    const response = await api().post("/package/cargo-prep-box-photo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    if (error.response) return { success: false, message: error.response.data?.message || "Failed to save box photo" };
    throw error;
  }
}

// Lightweight patch for updating only specific fields
export async function PatchPackageFields(packageId, fieldsToUpdate) {
  try {
    const response = await api().patch("/package/patch-fields", {
      _id: packageId,
      ...fieldsToUpdate
    });
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        success: true,
        data: response.data.data,
        message: response.data.message,
        fieldsUpdated: response.data.fieldsUpdated
      };
    } else if (!response.data.success) {
      return {
        status: response.status || 400,
        success: false,
        message: response.data.message || "Failed to update package"
      };
    }
    
    return response.data;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response) {
      return {
        status: error.response.status,
        success: false,
        message: error.response.data?.message || "Failed to update package"
      };
    }
    
    throw error;
  }
}

export async function deletePackagePhoto(trackingNumber, photoPath) {
  try {
    const response = await api().post("/package/delete-photo", {
      trackingNumber,
      photoPath,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
} 

export async function StorePackage(
  tracking,
  description = "",
  orderId = "",
  customerId = "",
  amount = 0,
  quantity = 1,
  shippingFee = 0,
  routeId,
  status = "Pending",
  remark = "",
  paymentStatus = false,
  station = "STD",
  whatsappStatus,
  isCOD,
  isDeliveryRequired,
  isForwardRequired,
  isSubCustomer,
  agentId,
  level,
  isSelfOrder,
  forwardAddress,
  deliveryAddress,
  finalShippingFee,
  transitCheckRequired,
  finalCheckRequired,
  noTrackingYet = false
) {
  try {
    let upperCaseTracking = noTrackingYet ? undefined : tracking.toUpperCase();
    const response = await api().post("/package/store", {
      tracking: upperCaseTracking,
      noTrackingYet,
      description,
      orderId,
      customerId,
      amount,
      quantity,
      shippingFee: parseInt(shippingFee),
      routeId,
      status,
      remark,
      paymentStatus,
      station,
      whatsappStatus,
      isCOD,
      isDeliveryRequired,
      isForwardRequired,
      isSubCustomer,
      agentId,
      level,
      isSelfOrder,
      forwardAddress,
      deliveryAddress,
      finalShippingFee,
      transitCheckRequired,
      finalCheckRequired,
    });
    
    // Handle new backend response format
    if (response.status === 201 && response.data.success) {
      return {
        status: 201,
        data: response.data.data,
        message: response.data.message
      };
    } else if (!response.data.success) {
      return {
        status: response.status || 400,
        message: response.data.message || "Failed to create package"
      };
    }
    
    return response.data;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response) {
      return {
        status: error.response.status,
        message: error.response.data?.message || "Failed to create package"
      };
    }
    
    throw error;
  }
}

export async function GetPackageList(page, pageSize, sort, search, filters = {}) {
  try {
    const { customerId, station, status, fromDate, toDate } = filters;
    const response = await api().get("/package/list", {
      params: {
        page,
        pageSize,
        sort: JSON.stringify(sort),
        search,
        customerId,
        station,
        status,
        fromDate,
        toDate,
      },
    });
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        data: response.data.data,
        total: response.data.total,
        pagination: response.data.pagination
      };
    } else if (!response.data.success) {
      return {
        status: 400,
        message: response.data.message || "Failed to get packages"
      };
    }
    
    return response;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response?.status === 400) {
      return {
        status: 400,
        message: error.response.data?.message || "Failed to get packages"
      };
    }
    
    throw error;
  }
}

export async function GetUnassignedPackageList(station, route) {
  try {
    const response = await api().get(
      "/package/list-unassigned-package-by-station-and-route",
      {
        params: {
          nextStationId: station,
          routeId: route,
        },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
  }
}

export async function GetMatchPackageList(station, route) {
  try {
    const response = await api().get(
      "/package/list-match-package-by-station-and-route",
      {
        params: {
          nextStationId: station,
          routeId: route,
        },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
  }
}

export async function GetMatchPackageListByCustomer(customerId) {
  try {
    const response = await api().get("/package/match-package-by-customer", {
      params: {
        customerId,
      },
    });
    return response;
  } catch (error) {
    console.error(error);
  }
}

export async function GetPackagesSumByStatus(customerId) {
  try {
    const response = await api().get("/package/sum-by-status", {
      params: {
        customerId,
      },
    });
    return response;
  } catch (error) {
    console.error(error);
  }
}
export async function GetPackagesByCustomerId(customerId, page = 0, pageSize = 20, sort = {}, search = "") {
  try {
    const response = await api().get("/package/customer", {
      params: {
        customerId,
        page,
        pageSize,
        sort: JSON.stringify(sort),
        search,
      },
    });
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        data: response.data.data,
        total: response.data.total
      };
    } else if (!response.data.success) {
      return {
        status: 400,
        message: response.data.message || "Failed to get packages"
      };
    }
    
    return response;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response?.status === 400) {
      return {
        status: 400,
        message: error.response.data?.message || "Failed to get packages"
      };
    }
    
    throw error;
  }
}
export async function GetPackagesByStationAndDate(
  fromDate = "",
  toDate = "",
  station = ""
) {
  // from & to format is YYYY-MM-DD
  // station - if empty will return all stations
  try {
    const response = await api().get("/package/list-by-station-and-date", {
      params: {
        fromDate,
        toDate,
        station,
      },
    });
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        data: response.data.data,
        total: response.data.total
      };
    } else if (!response.data.success) {
      return {
        status: 400,
        message: response.data.message || "Failed to get packages"
      };
    }
    
    return response;
  } catch (error) {
    console.log(error);
    
    // Handle error responses
    if (error.response?.status === 400) {
      return {
        status: 400,
        message: error.response.data?.message || "Failed to get packages"
      };
    }
    
    throw error;
  }
}

export async function GetSumPackagesForWhatsApp(station) {
  try {
    const response = await api().get("/package/sumpackages", {
      params: {
        station,
      },
    });

    return response;
  } catch (error) {
    console.log(error);
  }
}

export async function GetSumPackagesForAgent() {
  try {
    const response = await api().get("/package/sumpackages-agent");
    return response;
  } catch (error) {
    console.log(error);
  }
}
export async function GetSumPackagesForBulkMove(
  routeId,
  nextStationId,
  search
) {
  try {
    const response = await api().get("/package/sumpackages-bulkmove", {
      params: {
        routeId,
        nextStationId,
        search,
      },
    });

    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.log(error);
    return { success: false, data: [], message: error?.response?.data?.message || error.message };
  }
}

export async function GetSumPackagesByStation(stationId) {
  try {
    const response = await api().get("/package/sumpackages-by-station", {
      params: {
        stationId,
      },
    });
    
    // Handle successful response
    if (response.status === 200) {
      return {
        status: 200,
        data: response.data
      };
    }
    
    return response;
  } catch (error) {
    console.error("GetSumPackagesByStation error:", error);
    
    // Handle error responses
    if (error.response) {
      return {
        status: error.response.status,
        message: error.response.data?.message || "Failed to get package summary by station",
        data: error.response.data
      };
    }
    
    // Handle network or other errors
    throw error;
  }
}

export async function GetSumPackagesForForwardPackages(stationId) {
  try {
    const response = await api().get(
      "/package/sumpackages-for-forward-packages",
      {
        params: {
          stationId,
        },
      }
    );
    return response;
  } catch (error) {
    console.log(error);
  }
}

export async function GetSumPackagesForHomeDeliveryPackages(stationId) {
  try {
    const response = await api().get(
      "/package/sumpackages-for-home-delivery-packages",
      {
        params: {
          stationId,
        },
      }
    );
    return response;
  } catch (error) {
    console.log(error);
  }
}

export async function GetSumPackagesForDelivered(station, search, cancelToken) {
  try {
    const response = await api().get("/package/sumpackages-delivered", {
      params: {
        station,
        search,
      },
      cancelToken,
    });

    return response;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log("API call cancelled");
    } else {
      console.log(error);
    }
  }
}

export async function GetPackageDetailByTracking(tracking) {
  try {
    const response = await api().get("/package/tracking", {
      params: {
        tracking: tracking,
      },
    });
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        data: response.data.data
      };
    } else if (response.status === 404 || !response.data.success) {
      return {
        status: 202, // Maintain backward compatibility - frontend expects 202 for not found
        data: { message: response.data.message || "Package not found" }
      };
    }
    
    return response;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response?.status === 404) {
      return {
        status: 202, // Maintain backward compatibility - frontend expects 202 for not found
        data: { message: error.response.data?.message || "Package not found" }
      };
    } else if (error.response?.status === 400) {
      return {
        status: 400,
        message: error.response.data?.message || "Bad request"
      };
    }
    
    throw error;
  }
}

export async function GetPackageDetailById(id) {
  try {
    const response = await api().get(`/package/${id}`);
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        data: response.data.data
      };
    } else if (response.status === 404 || !response.data.success) {
      return {
        status: 404,
        message: response.data.message || "Package not found"
      };
    }
    
    return response;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response?.status === 404) {
      return {
        status: 404,
        message: error.response.data?.message || "Package not found"
      };
    } else if (error.response?.status === 400) {
      return {
        status: 400,
        message: error.response.data?.message || "Bad request"
      };
    }
    
    throw error;
  }
}

export async function GetReadyToPickupPackages(customerId, stationId) {
  try {
    const response = await api().get(
      "/package/ready-to-pickup?customerId=" +
        customerId +
        "&stationId=" +
        stationId
    );
    return response;
  } catch (error) {
    console.error(error);
  }
}

export async function GetSumReadyToPickupPackages(
  customerId,
  trackingNumbers,
  stationId,
  sendMessage = false,
  header, // optional: override the message's first line (e.g. courier-handoff header)
  intro // optional: override the explanatory line(s) under the date
) {
  try {
    console.log(customerId, trackingNumbers);
    const response = await api().post("/package/sum-ready-to-pickup", {
      customerId,
      trackingNumbers,
      stationId,
      sendMessage,
      ...(header ? { header } : {}),
      ...(intro ? { intro } : {}),
    });

    return response;
  } catch (error) {
    console.error(error);
  }
}

export async function DeletePackage(id) {
  try {
    const response = await api().delete("/package/delete/" + id);
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        data: response.data.data,
        message: response.data.message
      };
    } else if (!response.data.success) {
      return {
        status: response.status || 400,
        message: response.data.message || "Failed to delete package"
      };
    }
    
    return response.data;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response) {
      return {
        status: error.response.status,
        message: error.response.data?.message || "Failed to delete package"
      };
    }
    
    throw error;
  }
}

// Staff-triggered cancellation — see backend/services/packageCancelService.js.
// Does not touch station/stationSince and never auto-reverses money already
// collected (refundStatus is just flagged for the existing manual refund flow).
// Lives under /api/package-cancel (its own permission — see
// backend/scripts/seed-package-cancel-permissions.js), not /api/package.
export async function CancelPackage(id, reason, note) {
  try {
    const response = await api().patch(`/package-cancel/${id}/cancel`, { reason, note });
    if (response.status === 200 && response.data.success) {
      return { status: 200, data: response.data.data, message: response.data.message };
    }
    return { status: response.status || 400, message: response.data?.message || "Failed to cancel package" };
  } catch (error) {
    if (error.response) {
      return {
        status: error.response.status,
        code: error.response.data?.code,
        message: error.response.data?.message || "Failed to cancel package",
      };
    }
    throw error;
  }
}

// Undoes CancelPackage — restores whatever status the package had right
// before it was cancelled. Only valid while status is "Cancelled".
export async function ReactivatePackage(id) {
  try {
    const response = await api().patch(`/package-cancel/${id}/reactivate`, {});
    if (response.status === 200 && response.data.success) {
      return { status: 200, data: response.data.data, message: response.data.message };
    }
    return { status: response.status || 400, message: response.data?.message || "Failed to reactivate package" };
  } catch (error) {
    if (error.response) {
      return {
        status: error.response.status,
        code: error.response.data?.code,
        message: error.response.data?.message || "Failed to reactivate package",
      };
    }
    throw error;
  }
}

export async function scanPackage(tracking, nextStationId, actionType) {
  const response= await api()
    .post("/package/scan", {
      tracking,
      nextStationId,
      actionType,
    })
    .then((res) => res)
    .catch((error) => error.response);
  return response.data;
}

export async function matchPackage(originTrackingNumber, newTrackingNumber) {
  const responseData = await api()
    .post("/package/match-package", {
      originTrackingNumber,
      newTrackingNumber,
    })
    .then((res) => res)
    .catch((error) => error.response);
  return responseData;
}

export async function PatchPackage(
  _id,
  tracking,
  description,
  orderId,
  customerId,
  amount,
  quantity,
  shippingFee,
  routeId,
  station,
  status,
  remark,
  paymentStatus,
  whatsappStatus,
  isCOD,
  isDeliveryRequired,
  isForwardRequired,
  isSubCustomer,
  agentId,
  level,
  isSelfOrder,
  finalShippingFee,
  agentWhatsappStatus,
  forwardAddress,
  deliveryAddress,
  photos
) {
  try {
    // If called with a single FormData argument (for file uploads)
    if (_id instanceof FormData) {
      const response = await api().patch("/package/patch", _id, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Handle new backend response format for FormData
      if (response.status === 200 && response.data.success) {
        return {
          status: 200,
          data: response.data.data,
          message: response.data.message
        };
      } else if (!response.data.success) {
        return {
          status: response.status || 400,
          message: response.data.message || "Failed to update package"
        };
      }
      
      return response.data;
    }

    // Otherwise, use the original JSON body
    let upperCaseTracking = tracking?.toUpperCase?.() || "";
    const response = await api().patch("/package/patch", {
      _id,
      tracking: upperCaseTracking,
      description,
      orderId,
      customerId,
      amount,
      quantity,
      shippingFee,
      routeId,
      station,
      status,
      remark,
      paymentStatus,
      whatsappStatus,
      isCOD,
      isDeliveryRequired,
      isForwardRequired,
      isSubCustomer,
      agentId,
      level,
      isSelfOrder,
      finalShippingFee,
      agentWhatsappStatus,
      forwardAddress,
      deliveryAddress,
      photos,
    });
    
    // Handle new backend response format
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        data: response.data.data,
        message: response.data.message
      };
    } else if (!response.data.success) {
      return {
        status: response.status || 400,
        message: response.data.message || "Failed to update package"
      };
    }
    
    return response.data;
  } catch (error) {
    console.error(error);
    
    // Handle error responses
    if (error.response) {
      return {
        status: error.response.status,
        message: error.response.data?.message || "Failed to update package"
      };
    }
    
    throw error;
  }
}
// Narrow station/status-only update — "Move to End" and WhatsApp Self-Pickup's
// "mark as complete" use this, NOT PatchPackage. Split onto its own backend
// mount (/api/package-delivery-status) so it can carry its own permission,
// separate from the manager-approval-gated Edit Package flow (see PatchPackage
// and SubmitPackageChangeRequest below).
export async function PatchPackageDelivery(
  _id,
  station,
  status,
  paymentStatus,
  whatsappStatus,
  deliveryMethod
) {
  try {
    const response = await api()
      .patch(`/package-delivery-status/${_id}`, {
        station,
        status,
        paymentStatus,
        whatsappStatus,
        deliveryMethod,
      })
      .then((res) => res.data)
      .catch((error) => error.response);
    return response;
  } catch (error) {
    console.log(error);
  }
}

// Arrival WhatsApp board "have you notified the customer?" confirm — only
// station/status/remark/paymentStatus/whatsappStatus ever actually change, so
// this hits the narrow /api/package-ops mount rather than full-edit
// /api/package (see backend/controllers/packageOps.js#markArrivalNotified).
export async function PatchPackageSomeInfo(
  _id,
  tracking,
  station,
  status,
  remark,
  paymentStatus,
  whatsappStatus
) {
  try {
    const response = await api()
      .patch(`/package-ops/${_id}/arrival-notified`, {
        station,
        status,
        remark,
        paymentStatus,
        whatsappStatus,
      })
      .then((res) => res.data)
      .catch((error) => error.response);
    return response;
  } catch (error) {
    console.log(error);
  }
}

// Agent WhatsApp board "have you notified the agent?" confirm — see
// backend/controllers/packageOps.js#markAgentNotified.
export async function PatchPackageAgentWhatsappStatus(
  _id,
  tracking,
  agentWhatsappStatus,
  whatsappStatus = false,
  paymentStatus = false
) {
  try {
    const response = await api()
      .patch(`/package-ops/${_id}/agent-notified`, {
        agentWhatsappStatus,
        whatsappStatus,
        paymentStatus,
      })
      .then((res) => res.data)
      .catch((error) => error.response);
    return response;
  } catch (error) {
    console.log(error);
  }
}

// Shipping Fee page + sort-station COD/Fanshine/subcustomer fee prompt — only
// shippingFee ever actually changes (tracking/orderId/description/remark/amount/
// quantity were historically echoed back unchanged). See
// backend/controllers/packageOps.js#updateShippingFee.
export async function PatchShippingFee(
  _id,
  tracking,
  orderId,
  description,
  remark,
  amount,
  quantity,
  shippingFee
) {
  try {
    // Backend's isDefinedAndNumber only accepts numeric *strings* — coerce
    // defensively regardless of what the caller passes in.
    const response = await api()
      .patch(`/package-ops/${_id}/shipping-fee`, { shippingFee: String(shippingFee) })
      .then((res) => res.data)
      .catch((error) => error.response);
    return response;
  } catch (error) {
    console.log(error);
  }
}

// Match Package screen's quick-fill of orderId/description WITHOUT merging
// tracking numbers (the merge itself is MatchPackage() → POST
// /package/match-package). See
// backend/controllers/packageOps.js#updateMatchDetails.
export async function PatchMatchPackage(_id, tracking, orderId, description) {
  try {
    const response = await api()
      .patch(`/package-ops/${_id}/match-details`, { orderId, description })
      .then((res) => res.data)
      .catch((error) => error.response);
    return response;
  } catch (error) {
    console.log(error);
  }
}

// Scan-time Edit dialog on a parcel with no customer/route yet — fills in the
// missing data directly instead of going through admin approval. See
// backend/controllers/packageOps.js#scanAssign.
export async function PatchPackageScanAssign(_id, fields) {
  try {
    const response = await api()
      .patch(`/package-ops/${_id}/scan-assign`, fields)
      .then((res) => res.data)
      .catch((error) => error.response?.data || { success: false, message: error.message });
    return response;
  } catch (error) {
    console.log(error);
  }
}

// Final-station scan self-order tagging — optionally also collects the
// shipping fee in the same step if it was still 0. See
// backend/controllers/packageOps.js#markSelfOrder.
export async function MarkPackageSelfOrder(_id, shippingFee) {
  try {
    const body = { isSelfOrder: true };
    // Backend's isDefinedAndNumber only accepts numeric *strings*.
    if (shippingFee !== undefined) body.shippingFee = String(shippingFee);
    const response = await api()
      .patch(`/package-ops/${_id}/self-order`, body)
      .then((res) => res.data)
      .catch((error) => error.response);
    return response;
  } catch (error) {
    console.log(error);
  }
}

export async function GetPackageLogs(packageId) {
  try {
    const response = await api().get(`/package/${packageId}/logs`);
    return { status: response.status, data: response.data.data };
  } catch (error) {
    return { status: error.response?.status || 500, data: [] };
  }
}

export async function GetPackageWhatsAppLogs(packageId) {
  try {
    const response = await api().get(`/package/${packageId}/whatsapp-logs`);
    return { status: response.status, data: response.data.data };
  } catch (error) {
    return { status: error.response?.status || 500, data: [] };
  }
}

export async function BulkUpdatePackage(trackingNumbers, nextStation) {
  try {
    const response = await api().post("/package/bulk-update", {
      trackingNumbers,
      nextStation,
    });
    return response.data;
  } catch (error) {
    console.error("BulkUpdatePackage error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to bulk update packages"
    };
  }
}
